import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Settings from '../../assets/Settings.png';
import { CircularProgress } from '@mui/material';

import ArtistDetailsReports from './ArtistDetailsReports';
import ArtistDetailsFinancials from './ArtistDetailsFinancials';
import ArtistDetailsBankDetails from './ArtistDetailsBankDetails';
import ArtistDetailsProfile from './ArtistDetailsProfile';

import ArtistDetailsDashboard from './ArtistDetailsDashboard';
import NavBar from '../../components/NavBarSearchSong';
import Dropdown from '../../components/Dropdown';
import { artistActions } from '../../actions';
import { generalConstants } from '../../constants';
import { filter, map } from 'lodash';
import { Button } from '../../components';
import Popup from '../../components/Popup';
import { _showAlert } from '../../utils';
import { ArtistDetailsUploadedMusic } from './UploadMusic';

const ArtistDetails = () => {
  const history = useHistory();

  // Get Query Params from the URL
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const active_tab = queryParams.get('active');
  const { artist: artist_id } = useParams();
  const [isLoading, setIsLoading] = useState(true);

  const [value, setValue] = React.useState(active_tab ? active_tab : 'dashboard');
  const [artist, setArtist] = React.useState({});
  const [songs, setSongs] = React.useState([]);
  const [songStatus, setSongStatus] = React.useState(generalConstants.SUBMISSION_STATUS[0]);

  const [withDraws, setWithDraws] = React.useState([]);
  const [withDrawStatus, setWithDrawStatus] = React.useState(generalConstants.SUBMISSION_STATUS[0]);
  const [deletePopUp, setDeletePopUp] = React.useState({ isOpen: false, id: 0 });
  const [isArtistDeleting, setIsArtistDeleting] = useState(false);
  const [dashboardData, setDashboardData] = React.useState([]);
  const [isDashboardLoading, setIsDashboardLoading] = React.useState(false);
  const [dashboardError, setDashboardError] = React.useState(null);
  const [filterValue, setFilterValue] = React.useState(generalConstants.GRAPH_FILTER[0]);

  const ref = React.useRef(false);

  const handleChange = (event, newValue) => {
    history.push(`/artists/${artist_id}?active=${newValue}`);
    setValue(newValue);
  };

  const handleArtistDelete = async () => {
    setIsArtistDeleting(true);
    const artistDeleteRes = await artistActions.deleteArtist(deletePopUp.id, true);
    if (artistDeleteRes) {
      _showAlert('Artist Deleted Successfully', 'success');
      history.push('/artists');
    } else {
      _showAlert('Something went wrong', 'error');
      setIsArtistDeleting(false);
    }
  };

  let componentToRender;

  switch (active_tab) {
    case 'financials':
      componentToRender = <Dropdown value={withDrawStatus} setValue={setWithDrawStatus} options={generalConstants.SUBMISSION_STATUS} />;
      break;
    case 'uploaded_music':
      componentToRender = <Dropdown value={songStatus} setValue={setSongStatus} options={generalConstants.SUBMISSION_STATUS} />;
      break;
    case 'profile':
      componentToRender = (
        <div className="button-group">
          <Button
            className="button transparent"
            style={{ '--button-color': '#6B3EB4' }}
            onClick={() => setDeletePopUp({ isOpen: true, id: artist.id })}
          >
            delete Artist
          </Button>
        </div>
      );
      break;

    case 'dashboard':
      componentToRender = <Dropdown value={filterValue} setValue={setFilterValue} options={generalConstants.GRAPH_FILTER} />;
      break;
    default:
      componentToRender = <></>;
      break;
  }

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      const [artistDataRes, songsRes, withDrawsRes] = await Promise.allSettled([
        artistActions.fetchArtistById(artist_id),
        artistActions.fetchArtistSongs(artist_id),
        artistActions.fetchArtistWithdrawals(artist_id),
      ]);

      setArtist(artistDataRes.value);
      setSongs(songsRes.value);
      setWithDraws(withDrawsRes.value);

      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsDashboardLoading(true);
      setDashboardError(null);
      try {
        const data = await artistActions.fetchDashboard({
          artist_id: artist_id,
          filter: filterValue.value,
        });
        setDashboardData(data || []);
      } catch (error) {
        setDashboardError(error);
        setDashboardData([]);
      } finally {
        setIsDashboardLoading(false);
      }
    };
    fetchData();
  }, [filterValue.value]);

  return (
    <>
      <NavBar
        data="All Artist"
        icon={Settings}
        currentRouteName={artist?.first_name}
        breadcrumbsPath={[
          { name: 'Artist', link: '/artists' },
          { name: 'All Artist', link: '/artists' },
        ]}
      />
      <TabContext value={value}>
        <div className="tab-wrapper">
          <TabList
            onChange={handleChange}
            aria-label="lab API tabs example"
            className="forplay-tab"
            sx={{
              marginBottom: '40px',
              marginTop: '24px',
              '& .MuiTabs-flexContainer': {
                columnGap: '10px',
              },
              '& .MuiTab-root': {
                fontFamily: 'Poppins',
                fontWeight: '600',
                fontSize: '12px',
                lineHeight: '16px',
                textTransform: 'capitalize',
                paddingInline: '16px',
              },
              '& .MuiTab-root.Mui-selected': {
                color: '#6B3EB4',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#6B3EB4',
              },
            }}
          >
            <Tab label="Dashboard" value="dashboard" />
            <Tab label="Uploaded Music" value="uploaded_music" />
            <Tab label="Reports" value="reports" />
            <Tab label="Financials" value="financials" />
            <Tab label="Bank Details" value="bank_details" />
            <Tab label="Profile" value="profile" />
          </TabList>
          {componentToRender}
        </div>

        {isLoading ? (
          <div style={{ margin: 'auto', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress />
          </div>
        ) : (
          <div className="content">
            <TabPanel sx={{ padding: 0, overflow: 'scroll', flex: 1 }} value="dashboard">
              <ArtistDetailsDashboard 
                artist={artist} 
                setArtist={setArtist} 
                dashboardData={dashboardData} 
                filter={filterValue.value}
                isLoading={isDashboardLoading}
                error={dashboardError}
              />
            </TabPanel>
            <TabPanel sx={{ padding: 0, overflow: 'hidden', flex: 1, width: '100%' }} value="uploaded_music">
              <ArtistDetailsUploadedMusic songStatus={songStatus} songs={songs} setSongs={setSongs} isLoading={isLoading} />
            </TabPanel>
            <TabPanel sx={{ padding: 0, overflow: 'hidden', flex: 1, width: '100%' }} value="reports">
              <ArtistDetailsReports
                isLoading={isLoading}
                songs={map(
                  filter(songs, (song) => song.submission_status === 'ACCEPTED'),
                  (i, index) => ({
                    ...i,
                    result: `#${index + 1}`,
                  })
                )}
              />
            </TabPanel>
            <TabPanel sx={{ padding: 0, overflow: 'hidden', flex: 1, width: '100%' }} value="financials">
              <ArtistDetailsFinancials
                withDraws={withDraws}
                setWithDraws={setWithDraws}
                artist={artist}
                withDrawStatus={withDrawStatus}
                isLoading={isLoading}
              />
            </TabPanel>
            <TabPanel sx={{ padding: 0, overflow: 'hidden', flex: 1 }} value="bank_details">
              <ArtistDetailsBankDetails artist={artist} setArtist={setArtist} />
            </TabPanel>
            <TabPanel sx={{ padding: 0, overflow: 'hidden', flex: 1 }} value="profile">
              <ArtistDetailsProfile artist={artist} setArtist={setArtist} />
            </TabPanel>
          </div>
        )}
      </TabContext>

      <Popup open={deletePopUp?.isOpen} onClose={() => !isArtistDeleting && setDeletePopUp({ isOpen: false, id: null })}>
        <h4 style={{ textAlign: 'center', marginBottom: 20, fontSize: 20, lineHeight: '32px', maxWidth: '60%', marginInline: 'auto' }}>
          Are you sure, you want to delete this playlist?
        </h4>
        <div className="button-group" style={{ '--button-color': '#6900af' }}>
          <Button onClick={() => setDeletePopUp({ isOpen: false, id: null })} className="button transparent">
            Cancel
          </Button>

          <Button isLoading={isArtistDeleting} onClick={handleArtistDelete} className="button primary">
            Confirm
          </Button>
        </div>
      </Popup>
    </>
  );
};

export default ArtistDetails;

